import React, { useState } from 'react';
import { Card, Button, Table, Space, Tag, Input, Modal, Form, Typography, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNCRs, useCloseNCR } from '../../hooks/useQuality';
import { NCRRecord } from '../../services/quality.service';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const NCRsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  const [selectedNCR, setSelectedNCR] = useState<NCRRecord | null>(null);
  const [closeForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [severityFilter, setSeverityFilter] = useState<string | undefined>();

  const { data: ncrs = [], isLoading, refetch } = useNCRs({ status: statusFilter, severity: severityFilter });
  const closeMutation = useCloseNCR();

  const handleClose = (ncr: NCRRecord) => {
    setSelectedNCR(ncr);
    closeForm.resetFields();
    setIsCloseModalVisible(true);
  };

  const handleCloseSubmit = async (values: { notes?: string }) => {
    try {
      await closeMutation.mutateAsync({ id: selectedNCR!.id, notes: values.notes });
      setIsCloseModalVisible(false);
      closeForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error closing NCR:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Open: 'red',
      'Under Investigation': 'orange',
      'Root Cause Analysis': 'processing',
      'CAPA Assigned': 'blue',
      'Corrective Action': 'cyan',
      Verification: 'purple',
      Closed: 'success',
    };
    return colors[status] || 'default';
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      Critical: 'red',
      Major: 'orange',
      Minor: 'yellow',
    };
    return colors[severity] || 'default';
  };

  const filteredNCRs = ncrs.filter(
    (ncr) =>
      ncr.ncr_number.toLowerCase().includes(searchText.toLowerCase()) ||
      ncr.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      ncr.source?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: ColumnsType<NCRRecord> = [
    {
      title: 'NCR Number',
      dataIndex: 'ncr_number',
      key: 'ncr_number',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity: string) => <Tag color={getSeverityColor(severity)}>{severity}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Identified Date',
      dataIndex: 'identified_date',
      key: 'identified_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/qms/ncrs/${record.id}`)}
          >
            View
          </Button>
          {record.status !== 'Closed' && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={() => handleClose(record)}
            >
              Close
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Non-Conformance Reports (NCR)</Title>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
          <Space wrap>
            <Search
              placeholder="Search NCRs..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="Filter by Status"
              allowClear
              style={{ width: 180 }}
              onChange={setStatusFilter}
              value={statusFilter}
            >
              <Select.Option value="Open">Open</Select.Option>
              <Select.Option value="Under Investigation">Under Investigation</Select.Option>
              <Select.Option value="Root Cause Analysis">Root Cause Analysis</Select.Option>
              <Select.Option value="CAPA Assigned">CAPA Assigned</Select.Option>
              <Select.Option value="Corrective Action">Corrective Action</Select.Option>
              <Select.Option value="Verification">Verification</Select.Option>
              <Select.Option value="Closed">Closed</Select.Option>
            </Select>
            <Select
              placeholder="Filter by Severity"
              allowClear
              style={{ width: 150 }}
              onChange={setSeverityFilter}
              value={severityFilter}
            >
              <Select.Option value="Critical">Critical</Select.Option>
              <Select.Option value="Major">Major</Select.Option>
              <Select.Option value="Minor">Minor</Select.Option>
            </Select>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qms/ncrs/new')}>
            Create NCR
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredNCRs}
          loading={isLoading}
          rowKey="id"
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title="Close NCR"
        open={isCloseModalVisible}
        onCancel={() => {
          setIsCloseModalVisible(false);
          closeForm.resetFields();
        }}
        onOk={() => closeForm.submit()}
      >
        <Form form={closeForm} layout="vertical" onFinish={handleCloseSubmit}>
          <Form.Item name="notes" label="Closure Notes">
            <TextArea rows={4} placeholder="Enter closure notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NCRsPage;
